import { BiteLogDeviceEvent, BiteLogLoyaltyEvent, BiteLogType } from '@biteinc/enums';
import { Time } from '@biteinc/helpers';

function getTitle(biteLog) {
  const type = biteLog.get('type');
  let deviceName = biteLog.get('clientName');
  if (type === BiteLogType.Kiosk && biteLog.get('kioskName')) {
    deviceName = biteLog.get('kioskName');
  }

  const location =
    app.location || (biteLog.get('locationId') && app.locationList?.get(biteLog.get('locationId')));
  const timezone = location?.get('timezone') || Time.BITE_TZ;
  const timestamp = Time.stringFromTimestamp(biteLog.get('createdAt'), timezone, { short: true });

  return `[${timestamp}] ${deviceName}`;
}

app.BiteLogView = app.ModallableView.extend({
  className() {
    return 'bite-log-view';
  },

  initialize(options) {
    const biteLog = options.model;
    options.title = getTitle(biteLog);

    let description = biteLog.get('description');
    if (
      // show where loyalty coupon was redeemed when in org view
      biteLog.get('type') === BiteLogType.Loyalty &&
      [
        BiteLogLoyaltyEvent.CouponApplied,
        BiteLogLoyaltyEvent.CouponSingleRedemptionRedeemed,
      ].includes(biteLog.get('status')) &&
      app.locationList &&
      !app.location
    ) {
      const locationId = biteLog.get('locationId');
      const location = app.locationList.get(locationId);
      const locationName = location?.displayName();
      const orderId = biteLog.get('json')?.orderId;
      const orderUrl =
        location && orderId
          ? `${window.location.origin}/${location.attributes.orgNormalizedName}/${location.attributes.normalizedName}/${location.attributes.orderChannel}#orders/${orderId}`
          : '';

      if (orderId) {
        const orderLink = orderUrl
          ? `<a target="_blank" href="${orderUrl}">${orderId}</a>`
          : orderId;
        description += `\nRedeemed on Order: ${orderLink}\nAt ${locationName} (${locationId})`;
      }
    }

    options.views = [
      new app.JsonView({
        altText: 'Description',
        jsonValue: description,
      }),
    ];
    if (app.sessionUser.canViewFullSystemLogs() && Object.keys(biteLog.get('json') || {}).length) {
      options.views.push(
        new app.JsonView({
          altText: 'JSON',
          jsonValue: biteLog.get('json'),
        }),
      );
    }
    options.primaryFooterButton = {
      title: 'Copy Link',
      isEnabled: true,
      callback: () => {
        const url = `${window.location.href.split('#')[0]}#system-logs/${biteLog.id}`;
        if (navigator.clipboard.writeText(url)) {
          app.showSavedToast('Copied Link to This System Log!');
        }
      },
    };
    if (app.sessionUser.isBite()) {
      options.secondaryFooterButtons = {
        title: 'JSON / Logs / Analytics...',
        dropdownItemsByClassName: {
          viewJson: 'View JSON',
          ...(biteLog.get('kioskId') && {
            newRelic: 'This Kiosk: NewRelic Logs',
            postHogKiosk: 'This Kiosk: PostHog Analytics',
          }),
          ...(biteLog.get('orderId') && {
            postHogOrder: 'This Order: PostHog Analytics',
          }),
          kioskStatus: 'Kiosk Status from Around This Time',
          ...(biteLog.get('type') === BiteLogType.Device &&
            biteLog.get('status') === BiteLogDeviceEvent.NativeTransactionFinished && {
              createTransaction: 'Create Transaction from This Log',
            }),
        },
        clickHandler: ($button, className) => {
          switch (className) {
            case 'viewJson':
              const jsonView = new app.ModelJsonView({
                model: this.model,
                altText: 'Raw JSON',
                title: options.title,
              });
              app.modalManager.showModalWithView(jsonView);
              break;
            case 'newRelic':
              window.open(this.model.getNewRelicUrl());
              break;
            case 'postHogKiosk':
              window.open(this.model.getPostHogKioskUrl());
              break;
            case 'postHogOrder':
              window.open(this.model.getPostHogOrderUrl());
              break;
            case 'createTransaction':
              if (window.confirm('Are you sure you want to create a transaction from this log?')) {
                app.makeRequest(
                  'PUT',
                  `/api/v2/bite-logs/${biteLog.id}/insert-missing-transaction`,
                  null,
                  () => {
                    app.showSavedToast('Transaction created successfully!');
                  },
                );
              }
              break;
            case 'kioskStatus':
              {
                const createdAt = biteLog.get('createdAt');
                const afterAt = createdAt - Time.DAY;
                const kioskStatusList = new app.KioskStatusList([], {
                  url: `${app.apiPathForLocation('/status')}?before=${createdAt}&after=${afterAt}`,
                });
                const statusListView = new app.KioskStatusHistoryListView({
                  collection: kioskStatusList,
                  cellView: app.KioskCellView,
                  searchEnabled: false,
                  canCreate: false,
                });
                const location =
                  app.location ||
                  (biteLog.get('locationId') && app.locationList?.get(biteLog.get('locationId')));
                const timezone = location?.get('timezone') || Time.BITE_TZ;

                const modallableView = new app.ModallableView({
                  views: [statusListView],
                  title: `Kiosk Status from around ${Time.stringFromTimestamp(createdAt, timezone, {
                    short: true,
                  })}`,
                });
                app.modalManager.showModalWithView(modallableView);
              }
              break;
          }
          return false;
        },
      };
    }

    app.ModallableView.prototype.initialize.apply(this, arguments);
  },

  viewWasRemovedFromDom() {
    // This view is partly used as a details view, so to ensure that it gets cleaned up properly we
    // need to emit this event
    this.trigger(app.BaseDetailsView.Events.BaseDetailsViewDidClose, this);
  },

  render() {
    app.ModallableView.prototype.render.apply(this, arguments);

    const biteLog = this._options.model;
    if (biteLog.get('screenshotUrl')) {
      this.$el.append(`<img class="screenshot" src="${biteLog.get('screenshotUrl')}" />`);
    }

    return this;
  },
});
